import React, { useState, useEffect } from "react";
import {
  Button,
  Container,
  TextField,
  Table,
  TableBody,
  TableCell,
  Box,
  Modal,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  InputAdornment,
  AppBar,
  Toolbar,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useParams } from "react-router-dom";
import base64 from "base64-js";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Caste = () => {
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [canAdd, setCanAdd] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [casteToDelete, setCasteToDelete] = useState(null);
  const [castes, setCastes] = useState([]); // State to store castes data
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false); // Track loading state
  const [error, setError] = useState(null); // State to handle API errors
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];

  // Fetch caste data from the API
  const fetchCasteData = async () => {
    try {
      setLoading(true); // Show loading state
      setError(null); // Reset error state
      const apiUrl =
        "https://wsms.aidigitalsec.com/api/Caste/GetCaste";
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"), // Authorization token
        },
      });

      if (response.ok) {
        const data = await response.json(); // Parse JSON response
        if (data.data === null && data.msg === "Record Not Found") {
          setError("No records found"); // Set error message if no data is found
        } else {
          setCastes(data); // Update state with fetched data
        }
      } else {
        setError("Unable to fetch caste data"); // Set error message if API request fails
      }
    } catch (error) {
      console.error("API request error:", error);
      setError("An error occurred. Please try again later."); // Set error message for unexpected errors
    } finally {
      setLoading(false); // Hide loading state
    }
  };

  useEffect(() => {
    fetchCasteData(); // Fetch data when the component mounts
    Authorizer();
  }, []);



  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const { encodedFormId } = useParams();
  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId, }), // replace with actual formId if hardcoded
      });

      const result = await response.json();
      const data = result[0];

      // setAuthorization(data);
      setCanEdit(data?.uModify === 1);
      setCanAdd(data?.uWrite === 1);
      setCanDelete(data?.uDelete === 1);
    } catch (error) {
      console.error("Authorization check failed:", error);
    }
  };


  const handleClose = () => {
    setDeleteModalOpen(false);
  }
  const confirmDeleteCaste = (caste) => {
    setCasteToDelete(caste);
    setDeleteModalOpen(true);
  };



  // Navigate to the edit page with selected caste
  const handleEditClick = (caste) => {
    navigate(`/edit-caste/${caste.casteId}`, { state: { caste } });
  };

  // Handle delete caste
  const handleDeleteClick = async () => {
    try {
      const response = await fetch(
        `https://wsms.aidigitalsec.com/api/Caste/Id?CasteId=${casteToDelete.casteId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
        }
      );

      if (response.ok) {
        setSnackbarMessage('Caste deleted successfully!');
        setOpenSnackbar(true);
        setSnackbarSeverity("success")
        setCastes((prev) =>
          prev.filter((item) => item.casteId !== casteToDelete.casteId)
        );
      } else {
        // alert("Failed to delete caste");
        setSnackbarMessage('Failed to delete caste');
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
      }
    } catch (error) {
      console.error("Error:", error);
      // alert("Something went wrong.");
      setSnackbarMessage('Something went wrong.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
    } finally {
      setDeleteModalOpen(false);
      setCasteToDelete(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // reset to page 1
  };

  const filteredData = castes
    .filter((caste) =>
      caste.casteName.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Caste
          </Typography>
        </Toolbar>
      </AppBar>
      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6 }}>

        <div className="mb-3" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', gap: '0.5rem' }}>
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "300px" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">

                  <SearchIcon />
                </InputAdornment>
              ),
              style: { padding: "0px 8px" },
            }}
            sx={{ width: 300, backgroundColor: "#f9f9f9" }}
          />
          <Tooltip title={canAdd ? "Add Caste" : "Not Authorized to Add"} arrow>
            <span><Button
              variant="contained"
              color="primary"
              style={{
                width: "160px",
                height: "50px",
                padding: "10px 0",
                fontWeight: 'bold'
              }}

              onClick={() => navigate("/add-caste")} // Navigate to the Add Caste page
              disabled={!canAdd}
            >
              ADD Caste
            </Button></span></Tooltip>
        </div>

        <TableContainer component={Paper}>
          <Table sx={{
            minWidth: 650,
            border: '1px solid #ccc',
            '& .MuiTableCell-root': {
              borderRight: '1px solid #ccc',
              fontSize: '1rem',
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
            },
          }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold', fontSize: '1.1rem' }}>Caste</TableCell>
                <TableCell style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography variant="h6" color="error">
                      {error}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((caste) => (
                  <TableRow key={caste.casteId}>
                    <TableCell style={{ border: '1px solid #ddd', padding: '8px', fontSize: '1.0rem' }}>{caste.casteName}</TableCell>
                    <TableCell style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>
                      <Tooltip title={canEdit ? "Edit Caste" : "Not Authorized to Edit"} arrow>
                        <span>
                          <Button
                            variant="contained"
                            color="warning"
                            style={{ marginRight: '5px', fontSize: '1rem' }}
                            startIcon={<EditIcon />}
                            onClick={() => handleEditClick(caste)}
                            disabled={!canEdit}
                          >
                            <b>EDIT</b>
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={canDelete ? "Delete Caste" : "Not Authorized to Delete"} arrow>
                        <span>
                          <Button
                            variant="contained"
                            color="error"
                            style={{ marginLeft: '5px', fontSize: '1rem' }}
                            startIcon={<DeleteIcon />}
                            onClick={() => confirmDeleteCaste(caste)}
                            disabled={!canDelete}
                          >
                            <b>DELETE</b>
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No Castes Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredData.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
            <TextField
              select
              label="Rows per page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ width: 150 }}
              SelectProps={{
                native: true,
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>

            <Box>
              <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
                Page {page} of {Math.ceil(filteredData.length / rowsPerPage)}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 1}
                sx={{ mr: 1 }}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page + 1)}
                disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

      </Paper>
      <Modal open={deleteModalOpen} onClose={handleClose} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete caste <strong>{casteToDelete?.casteName}</strong>
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteClick}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Caste;
